import React from 'react';

import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar';

import SlideLeftIcon from '../../images/icons/slide-left.svg';
import SlideRightIcon from '../../images/icons/slide-right.svg';

import type { NavBarMenuItemGroups } from '../../types/common.types';

import { NavBarMenuItem } from './NavBarMenuItem.view';
import { NavBarFooter } from './NavBarFooter.view';
import { NavBarCollapsedFooter, NavBarExpendedFooter } from './NavBarFooters.view';

import { TestID } from '../../testID';

import './NavBar.css';

const useStyle = makeStyles((theme) => {
  return {
    navbar: {
      backgroundColor: theme.palette.background.light,
      borderLeftWidth: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
      borderTopWidth: 0,
      position: 'relative',
      width: '16rem',
    },
    navbarCollapsed: {
      width: '3.75rem',
      '& $navbarMenuLabel': {
        fontSize: '0.75rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
      },
      '& .navbar__footer': {
        fontSize: 'smaller',
        transform: 'rotate(-90deg) translateX(-16rem)',
        marginLeft: '-13rem',
        paddingTop: '14.5rem',
      },
    },
    navbarToggler: {
      display: 'block',
      backgroundColor: theme.palette.background.dark,
      borderWidth: '0',
      color: theme.palette.grey[50],
      padding: '0 0.75rem',
      textAlign: 'right',
      width: '100%',
      '&:focus': {
        outlineWidth: 0,
        color: theme.palette.grey[100],
      },
      '&:hover': {
        color: theme.palette.grey[100],
      },
      '& svg': {
        display: 'block',
        height: '2em',
        marginLeft: 'auto',
        padding: '0.25em 0',
        width: '2em',
      },
    },
    navbarMenu: {
      paddingLeft: 0,
      margin: 0,
    },
    navbarMenuPrimaryItem: {
      backgroundColor: theme.palette.background.middle,
      color: theme.palette.text.primary,
      display: 'block',
      padding: '0.5em 0.625em',
    },
    navbarMenuGroup: {
      display: 'block',
      '& ul': {
        padding: 0,
      },
    },
    navbarMenuLabel: {
      backgroundColor: theme.palette.background.semilight,
      color: theme.palette.text.primary,
      height: '2.125rem',
      lineHeight: '1.125rem',
      padding: '0.5rem 0.75rem',
    },
    navbarMenuItem: {
      display: 'block',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
      '&:hover $navbarMenuLink': {
        backgroundColor: 'transparent',
        color: theme.palette.common.white,
      },
    },
    navbarMenuLink: {
      alignItems: 'center',
      display: 'flex',
      color: theme.palette.text.primary,
      lineHeight: 2,
      overflow: 'hidden',
      padding: '0.25rem 0.75rem',
      position: 'relative',
      textDecoration: 'none',
      '& svg': {
        flex: '0 0 2rem',
        marginRight: '1.25em',
        maxHeight: '2rem',
        maxWidth: '2rem',
        width: '2rem',
        zIndex: '200',
      },
    },
    navbarMenuLinkActive: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    navbarMenuLinkDisabled: {
      opacity: '0.33',
    },
  };
});

export const NavBar: React.FC<{
  collapsed: boolean;
  handleToggle: () => void;
  primaryItem: React.ReactNode;
  navBarMenuItemGroups: NavBarMenuItemGroups;
  statusBar?: React.ReactNode;
}> = ({ collapsed, handleToggle, primaryItem, navBarMenuItemGroups, statusBar }) => {
  const classes = useStyle();

  return (
    <div
      data-testid={collapsed ? TestID.NavBarCollapsed : TestID.NavBarExpended}
      className={cx(classes.navbar, collapsed && classes.navbarCollapsed)}
    >
      <PerfectScrollbar>
        <button
          className={classes.navbarToggler}
          onClick={handleToggle}
          title="Toggle menu"
          data-testid={TestID.NavBarToggler}
        >
          {collapsed ? <SlideRightIcon /> : <SlideLeftIcon />}
        </button>
        <ul className={classes.navbarMenu}>
          {primaryItem && <li className={classes.navbarMenuPrimaryItem}>{primaryItem}</li>}
          {navBarMenuItemGroups.map((navBarMenuItemGroup) => (
            <NavBarMenuItem
              key={navBarMenuItemGroup.id}
              menuItemGroup={navBarMenuItemGroup}
              classes={classes}
            />
          ))}
        </ul>
        <NavBarFooter
          footer={
            collapsed ? <NavBarCollapsedFooter /> : <NavBarExpendedFooter statusBar={statusBar} />
          }
        />
        {statusBar}
      </PerfectScrollbar>
    </div>
  );
};
